import { Activity, Blocks, Boxes, Building2, Layers, ChartLine, CloudDownload, House, IdCard, KeyRound, LogOut, RadioTower, RefreshCw, Satellite, Signal, Ticket, Warehouse, type LucideIcon } from "lucide-react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useState } from "react";

import { auth } from "@/api/client";
import { AlertBanner } from "@/components/AlertBanner";
import { AphelionLogo } from "@/components/AphelionLogo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { GATE, useSession } from "@/auth/session";

interface ModuleDef {
  path: string;
  label: string;
  icon: LucideIcon;
  gate: number;
  internal?: boolean;
  wired: boolean; // false -> renders as SOON, not navigable
}

/** Rail = customer modules, then a PLATFORM section (Connect, Link).
 *  Called "Aphelion Internal" until 2026-09-24. Those modules are gated on
 *  capabilities in NEVER_FOR_CUSTOMERS — they belong to the super_admin
 *  ROLE, not to the company called Aphelion, which is CMP-001 like any
 *  other. The old label told every customer that one company outranked
 *  them. */
export const MODULES: ModuleDef[] = [
  { path: "home", label: "Home", icon: House, gate: 0, wired: true },
  // No gate: Plans shows the PRODUCT, not anybody's data — no company, asset or
  // person appears on it. A customer seeing what Pro includes is a sales
  // feature, not a leak.
  { path: "plans", label: "Plans", icon: Layers, gate: 0, wired: true },
  { path: "fleet", label: "Fleet", icon: Satellite, gate: GATE.FLEET, wired: true },
  { path: "analytics", label: "Analytics", icon: ChartLine, gate: GATE.ANALYTICS, wired: false },
  { path: "ops", label: "Ops", icon: Ticket, gate: GATE.OPS, wired: false },
  { path: "fota", label: "FOTA", icon: CloudDownload, gate: GATE.FOTA, wired: true },
  { path: "asset-data", label: "Asset Data", icon: Activity, gate: GATE.ASSET_DATA, wired: true },
  // Gated on GATE.API (1100) since 2026-09-22. This was `gate: 0` with a note
  // claiming Fabric granted 1100:false even to super_admin — checked against
  // the live session map on the box and that is not true: 1100 is granted.
  // Every permission is a page, and a page the map denies must not open.
  { path: "api", label: "API", icon: KeyRound, gate: GATE.API, wired: true },
  // Registry, Drivers and Asset Catalog had NO gate of their own until
  // 2026-09-22 — all three rode CONNECT, so granting one granted all three
  // plus Audit. `internal` below is cosmetic (a dot + rail grouping), never
  // access control.
  { path: "registry", label: "Registry", icon: Warehouse, gate: GATE.REGISTRY, internal: true, wired: true },
  { path: "drivers", label: "Drivers", icon: IdCard, gate: GATE.DRIVERS, internal: true, wired: true },
  { path: "asset-catalog", label: "Asset Catalog", icon: Blocks, gate: GATE.ASSET_CATALOG, internal: true, wired: true },
  // SOON, not wired: `connect/mock.ts` is invented SIM data and the module
  // makes ZERO API calls. Dattatray: "I dont want any emulated/hardcoded
  // stuff on screen." A module with no backend belongs in the SOON list
  // beside fleet/analytics/ops, not rendering a convincing fake fleet.
  { path: "connect", label: "Connect", icon: Signal, gate: GATE.CONNECT, internal: true, wired: false },
  { path: "link", label: "Link", icon: RadioTower, gate: GATE.LINK, internal: true, wired: true },
  // Organisations (D-88): parties, their people and role assignments. Gated
  // on 1700 and in NEVER_FOR_CUSTOMERS — it lists every party on the
  // platform, so a customer seeing it would see the other customers.
  { path: "organisations", label: "Organisations", icon: Building2, gate: GATE.ORGANISATIONS, internal: true, wired: true },
  { path: "products", label: "Products", icon: Boxes, gate: GATE.PRODUCTS, internal: true, wired: true },
];

/** Fabric's three role slugs, in the words a person uses for them. */
const ROLE_LABEL: Record<string, string> = {
  super_admin: "Super Admin",
  company_admin: "Company Admin",
  company_user: "User",
};

export function Shell() {
  const { me, can, logout, setMe } = useSession();
  const [busy, setBusy] = useState(false);

  /** The Refresh button: pull the permission map from Fabric immediately.
   *
   *  Permissions refresh on a 15-minute clock, which is right in the
   *  background and far too slow for the case that matters — an admin changes
   *  a grant and wants to see it. On a revoked session the server answers 401
   *  and the api layer drops us at the login page, which is the correct
   *  outcome, so there is nothing to handle here beyond clearing `busy`. */
  async function refresh() {
    setBusy(true);
    try {
      setMe(await auth.resync());
    } catch {
      /* 401 -> handled globally; anything else leaves the old map in place,
         which is strictly better than blanking the rail on a transient error */
    } finally {
      setBusy(false);
    }
  }
  const [collapsed, setCollapsed] = useState(false);
  const loc = useLocation();
  const current = MODULES.find((m) => loc.pathname.startsWith(`/${m.path}`));

  const renderNav = (m: ModuleDef) => {
    const allowed = m.gate === 0 || can(m.gate);
    const usable = allowed && m.wired;
    if (!usable) {
      return (
        <button key={m.path} className="nav" style={{ opacity: 0.45, cursor: "not-allowed" }} disabled>
          <span className="ic"><m.icon size={16} strokeWidth={1.9} aria-hidden /></span>
          <span className="tx">{m.label}</span>
          <span className="chip mute" style={{ marginLeft: "auto" }}>
            {allowed ? "SOON" : "LOCKED"}
          </span>
        </button>
      );
    }
    return (
      <NavLink key={m.path} to={`/${m.path}`} className={({ isActive }) => `nav${isActive ? " on" : ""}`}>
        <span className="ic"><m.icon size={16} strokeWidth={1.9} aria-hidden /></span>
        <span className="tx">{m.label}</span>
        {m.internal && <span className="idot" title="Platform - super admin only" />}
      </NavLink>
    );
  };

  return (
    <>
      <header className="appbar">
        {/* Animated mark - the same rotating planet as the login page, just
            small. `animated` is what draws the orbiting body; without it the
            logo is a static ellipse. */}
        <div className="brand">
          <AphelionLogo width={50} height={33} animated />
          {/* One line, not stacked. Stacked spent the bar's height on two
              lines and forced both to ~10-12px; side by side they read as a
              single lockup and both can be larger. */}
          <div className="brand-badge">
            <span className="brand-co">Aphelion Energy</span>
            <span className="brand-dot">·</span>
            <span className="brand-prod">Orbit Platform</span>
          </div>
        </div>

        <div className="appbar-right">
          <span className="status-ok" title="orbit-app is reachable">
            <span className="gdot" /> Server: Online
          </span>

          {me && (
            <span className="who">
              {/* Leading separator: the server status sits outside .who, so the
                  pipe that divides it from the identity strip belongs here - 
                  same element as the other two, so all three hide together on
                  narrow screens rather than leaving a stray bar. */}
              <span className="who-sep">|</span>
              <span className="who-email">{me.login_id}</span>
              <span className="who-sep">|</span>
              {/* The COMPANY, not its slug. /auth/me populates company_name
                  from the tenants row; the slug is the fallback for a company
                  Orbit has not recorded a name for yet.
                  A cross-company caller belongs to NO company, which is a
                  different fact from an unknown name - so it branches on the
                  flag and leaves the name/slug chain intact for everyone who
                  does have one. */}
              <span className="who-co">
                {me.cross_company ? "All companies" : (me.company_name ?? me.company_id)}
              </span>
              <span className="who-sep">|</span>
              <span>{ROLE_LABEL[me.role] ?? me.role}</span>
            </span>
          )}

          <ThemeToggle />

          <button
            className="btn-ok"
            onClick={() => void refresh()}
            disabled={busy}
            title="Re-read your permissions from Fabric now"
          >
            <RefreshCw size={14} strokeWidth={2} className={busy ? "spin" : undefined} />
            {busy ? "Refreshing" : "Refresh"}
          </button>

          <button className="btn-bad" onClick={() => void logout()}>
            <LogOut size={14} strokeWidth={2} />
            Logout
          </button>
        </div>
      </header>

      <div className="shell">
        <nav className={`rail${collapsed ? " collapsed" : ""}`} aria-label="Modules">
          <button className="nav" onClick={() => setCollapsed((c) => !c)} aria-label="Collapse sidebar">
            <span className="ic">{collapsed ? "»" : "«"}</span>
            <span className="tx" style={{ color: "var(--t-dim)" }}>
              Collapse
            </span>
          </button>
          <div className="rail-label">Modules</div>
          {MODULES.filter((m) => !m.internal).map(renderNav)}
          <div className="rail-label">Platform</div>
          {MODULES.filter((m) => m.internal).map(renderNav)}
          <div className="rail-foot">
            Users &amp; roles managed in Fabric
            <br />
            Orbit v0.1.0
          </div>
        </nav>

        <main className="main">
          {/* outage banner: only for users who can read the ops endpoint */}
          {can(GATE.LINK) && <AlertBanner />}
          {/* ROUTE-LEVEL ENFORCEMENT, not just a hidden nav entry. Hiding the
              rail item never stopped anyone typing /registry - the page would
              mount and every API call inside it would 403, which reads as a
              broken console rather than a refusal. Keyed off the SAME MODULES
              list the rail uses, so a new module cannot be added with a nav
              gate and no route gate.
              The server is still the enforcement point (OAU-05); this is UX. */}
          {current && current.gate !== 0 && !can(current.gate)
            ? <NoAccess label={current.label} />
            : <Outlet />}
        </main>
      </div>
    </>
  );
}

/** Shown when someone reaches a module's URL without its permission.
 *
 *  Deliberately says WHO can fix it. Orbit cannot grant anything — the
 *  permission map lives in Fabric (OAU-07, one admin surface) — so a message
 *  offering no next step would just strand the person. */
/** Shown wherever the permission map closes something — a module (route guard
 *  below) or a single sub-tab inside one (see ApiModule's Activity). Exported so
 *  a module never hand-rolls its own "denied" panel with different wording. */
export function NoAccess({ label }: { label: string }) {
  return (
    <div className="panel panel-pad" style={{ maxWidth: 520, marginTop: 24 }}>
      <div className="section-label">{label}</div>
      <p className="cap" style={{ lineHeight: 1.6 }}>
        You don't have access to this module. Ask your administrator to grant it
        in Fabric - it takes effect here within a few minutes, without signing
        out.
      </p>
    </div>
  );
}

/** Sub-tab row used by every module (Connections/Statistics/Debug, etc).
 *
 *  A tab may carry its own `gate` when it is more sensitive than the module
 *  around it — API's Activity tab reads the platform-wide audit trail, so it
 *  needs GATE.AUDIT on top of the module's GATE.API. Hiding the tab is
 *  cosmetic; the route guard beside it and `require_cap` on the server are what
 *  actually enforce it.
 */
export function SubNav({ tabs, base }: {
  tabs: { path: string; label: string; gate?: number }[]; base: string;
}) {
  const { can } = useSession();
  return (
    <nav className="msubnav">
      {tabs.filter((t) => !t.gate || can(t.gate)).map((t) => (
        <NavLink
          key={t.path}
          to={`${base}/${t.path}`}
          className={({ isActive }) => `stab${isActive ? " on" : ""}`}
        >
          {t.label}
        </NavLink>
      ))}
    </nav>
  );
}
